import { useState, useEffect } from "react"
import { useGlobalContext } from "../../data/GlobalContext"
import { formatDate } from "../request_history/TransactionHistory"
const apiUrl = process.env.REACT_APP_API_JSON_URL

export default function ShareCreditsReport() {
  const { username, fetchCreditReport } = useGlobalContext()

  const today = new Date().toISOString().split("T")[0]
  const [fromDate, setFromDate] = useState(today)
  const [toDate, setToDate] = useState(today)
  const [loadingId, setLoadingId] = useState(null)
  const [creditHistory, setCreditHistory] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  const handleFilter = async () => {
    if (!username) {
      alert("Username is missing.")
      return
    }
    try {
      const report = await fetchCreditReport(username, fromDate, toDate)
      setCreditHistory(report)
      // Reset to the first page when new data is fetched.
      setCurrentPage(1)
    } catch (error) {
      console.error("Error fetching summary report:", error)
    }
  }

  // Auto-load data when username becomes available.
  useEffect(() => {
    if (username) {
      handleFilter()
    }
  }, [username])

  // Compute indexes for slicing data for the current page.
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentCreditHistory = creditHistory.slice(
    indexOfFirstItem,
    indexOfLastItem
  )

  // Calculate total pages.
  const totalPages = Math.ceil(creditHistory.length / itemsPerPage)

  // Handlers for prev and next buttons.
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1)
    }
  }

  const handleCancel = async (id) => {
    try {
      const params = new URLSearchParams({
        cmd: "updateRequest",
        transId: id,
        status: "3",
        u: username,
      })

      const res = await fetch(`${apiUrl}?${params.toString()}`, {
        method: "GET",
      })

      if (!res.ok) {
        throw new Error(`Network response was not ok (${res.status})`)
      }

      const json = await res.json()
      if (json.s) {
        window.location.reload()
      } else {
        alert(json.e)
      }
    } catch (err) {
      console.error("Cancel request failed:", err)
      alert("An error occurred. Please try again.")
    }
  }

  const handleAccept = async (id) => {
    if (loadingId === id) return

    setLoadingId(id)
    try {
      const params = new URLSearchParams({
        cmd: "updateRequest",
        transId: id,
        status: "4",
        u: username,
      })

      const res = await fetch(`${apiUrl}?${params.toString()}`, {
        method: "GET",
      })

      if (!res.ok) {
        throw new Error(`Network response was not ok (${res.status})`)
      }

      const json = await res.json()
      if (json.s) {
        window.location.reload()
      } else {
        alert(json.e)
      }
    } catch (err) {
      console.error("Accept request failed:", err)
      alert("An error occurred. Please try again.")
    } finally {
      setLoadingId(null)
    }
  }

  const handleReject = async (id) => {
    try {
      const params = new URLSearchParams({
        cmd: "updateRequest",
        transId: id,
        status: "5",
        u: username,
      })

      const res = await fetch(`${apiUrl}?${params.toString()}`, {
        method: "GET",
      })

      if (!res.ok) {
        throw new Error(`Network response was not ok (${res.status})`)
      }

      const json = await res.json()
      if (json.s) {
        window.location.reload()
      } else {
        alert(json.e)
      }
    } catch (err) {
      console.error("Reject request failed:", err)
      alert("An error occurred. Please try again.")
    }
  }
  return (
    <>
      <div className="game-report-container">
        <div className="transaction-report-tools">
          <div className="transaction-report-datepicker">
            <label>From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="data-datepicker"
            />
          </div>
          <div className="transaction-report-datepicker">
            <label>To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="data-datepicker"
            />
          </div>
          <button
            onClick={handleFilter}
            className="transaction-report-filter-btn"
          >
            Continue
          </button>
        </div>

        <div className="share-credit-history-wrapper">
          <div className="share-credit-history-table">
            <div className="share-credit-history-head">
              <div>ID</div>
              <div>Date</div>
              <div>From</div>
              <div>To</div>
              <div>Amount</div>
              <div>Status</div>
            </div>
            <div className="share-credit-history-body">
              {currentCreditHistory.length > 0 ? (
                currentCreditHistory.map((tx, idx) => (
                  <div key={idx} className="share-history-results">
                    <div>{tx.reportID || "-"}</div>
                    <div>{formatDate(tx.reportDate) || "-"}</div>
                    <div>{tx.reportFromUser || "-"}</div>
                    <div>{tx.reportToUser || "-"}</div>
                    <div>{tx.transferAmount || "-"}</div>
                    <div>
                      {tx.statusCode === 0 ? (
                        // 0 = waiting for approval
                        tx.reportFromUser === username ? (
                          <p className="share-credit-cancel-container">
                            Waiting for approval
                            <button
                              type="button"
                              onClick={() => handleCancel(tx.reportID)}
                              className="share-credit-cancel"
                            >
                              Cancel
                            </button>
                          </p>
                        ) : (
                          <p>Waiting for approval</p>
                        )
                      ) : tx.statusCode === 1 ? (
                        // 1 = just-approved / pending accept-reject
                        username === tx.reportToUser ? (
                          <>
                            <div className="share-credits-admin">
                              <button
                                id={`accept${tx.reportID}`}
                                type="button"
                                onClick={() => handleAccept(tx.reportID)}
                                disabled={loadingId === tx.reportID} // disable while processing
                              >
                                {loadingId === tx.reportID
                                  ? "Processing..."
                                  : "Accept"}
                              </button>

                              <button
                                type="button"
                                onClick={() => handleReject(tx.reportID)}
                              >
                                Reject
                              </button>
                            </div>
                          </>
                        ) : (
                          <p className="success">Approved</p>
                        )
                      ) : tx.statusCode === 2 ? (
                        // 2 = Not approved
                        <p>{tx.reportStatus}</p>
                      ) : tx.statusCode === 3 ? (
                        // 3 = Cancelled
                        <p>{tx.reportStatus}</p>
                      ) : tx.statusCode === 4 ? (
                        // 4 = Transferred
                        <p>{tx.reportStatus}</p>
                      ) : (
                        <p>{tx.reportStatus}</p>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="game-report-error-message">
                  No data available for the selected period.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Pagination Controls */}
        {creditHistory.length > itemsPerPage && (
          <div className="pagination">
            <button onClick={handlePrevPage} disabled={currentPage === 1}>
              Prev
            </button>
            <div>
              Page {currentPage} of {totalPages}
            </div>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}

        {fromDate === "" && toDate === "" && (
          <div className="game-report-disclaimer-message">
            ( Data's from about a week ago—pick a date to see more )
          </div>
        )}
      </div>
      <div className="share-credits-container">
        <h3>How to accept the request share credits?</h3>

        <ol>
          <li>From Share Credits Report</li>
          <li>Select the date and click SEARCH for pending request</li>
          <li>The user will be given two choices : Accept/Reject</li>
          <li>
            Once the user accepted, they will receive the amount directly to
            their balance.
          </li>
        </ol>
        <h3>Terms and Conditions</h3>
        <ul>
          <li>
            The receiver will need to complete a 1x rollover requirement of the
            amount they receive before withdrawal.
          </li>
          <li>We reserve the right to change the T&C without prior notice.</li>
          <li>
            For not approved/cancelled status, the amount was automatically refunded to the sender's FGH account.
          </li>
        </ul>
      </div>
    </>
  )
}
